import { LoadingIcon, LocationMapDialog } from '@/components/controls';
import {
  useDeleteSubject,
  useInsertSubject,
  useSubjects,
  useToggleSubjectKey,
} from '@/hooks';
import { getGroupType, spin } from '@/utils';
import {
  Autorenew as AutorenewIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { Masonry } from '@mui/lab';
import {
  alpha,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { HighPrioritySuspect } from './HighPrioritySuspect';
import { PrisonRecall } from './PrisonRecall';
import { Suspect } from './Suspect';
import { useNichePrisonRecalls } from './useNichePrisonRecalls';
import { useNicheSuspects } from './useNicheSuspects';
import { useNicheWarrants } from './useNicheWarrants';
import { Warrant } from './Warrant';

export function Subjects({ groupCodes, onComplete }) {
  const {
    query: {
      data: suspects,
      isFetching: isFetchingSuspects,
      isLoading: isLoadingSuspects,
      refetch: refetchSuspects,
    },
  } = useNicheSuspects(groupCodes);
  const {
    query: {
      data: prisonRecalls,
      isFetching: isFetchingPrisonRecalls,
      isLoading: isLoadingPrisonRecalls,
      refetch: refetchPrisonRecalls,
    },
  } = useNichePrisonRecalls(getGroupType('SECTOR'), groupCodes);
  const {
    query: {
      data: outstandingWarrants,
      isFetching: isFetchingWarrants,
      isLoading: isLoadingWarrants,
      refetch: refetchWarrants,
    },
  } = useNicheWarrants(getGroupType('SECTOR'), groupCodes);
  const {
    query: {
      data: highPrioritySuspects,
      isFetching: isFetchingHighPrioritySuspects,
      isLoading: isLoadingHighPrioritySuspects,
      refetch: refetchHighPrioritySuspects,
    },
  } = useSubjects('SUSPECT', getGroupType('SECTOR'), groupCodes, {
    highPriority: true,
  });
  const outstandingSuspects = useMemo(
    () =>
      suspects.filter(
        (suspect) =>
          !(highPrioritySuspects ?? []).some(
            (highPrioritySuspect) =>
              highPrioritySuspect.recordId === suspect.id,
          ),
      ),
    [suspects, highPrioritySuspects],
  );
  const { mutate: insertSubject } = useInsertSubject();
  const { mutate: toggleSubjectKey } = useToggleSubjectKey();
  const { mutate: deleteSubject } = useDeleteSubject();
  const { width, ref } = useResizeDetector();
  const highPriorityColumns = useMemo(
    () => Math.floor(width / 328) || 1,
    [width],
  );
  const lowPriorityColumns = useMemo(
    () => Math.floor(width / 248) || 1,
    [width],
  );
  const [selectedIndex, setSelectedIndex] = useState({
    type: null,
    recordId: null,
  });
  const selectedRecord = useMemo(() => {
    switch (selectedIndex.type) {
      case 'SUSPECT':
        return suspects.find(({ id }) => id === selectedIndex.recordId);
      case 'PRISON_RECALL':
        return prisonRecalls.find(({ id }) => id === selectedIndex.recordId);
      case 'WARRANT':
        return outstandingWarrants.find(
          ({ id }) => id === selectedIndex.recordId,
        );
      default:
        return null;
    }
  }, [selectedIndex, suspects, prisonRecalls, outstandingWarrants]);

  function handleRefreshClick() {
    refetchSuspects();
    refetchPrisonRecalls();
    refetchWarrants();
    refetchHighPrioritySuspects();
  }

  const handleShowMapClick = (type, id) => () => {
    setSelectedIndex(type, id);
  };

  function handleMapClose() {
    setSelectedIndex({
      type: null,
      recordId: null,
    });
  }

  function handleCompleteClick() {
    onComplete();
  }

  const handleOptionClick = (type, recordId, code, key) => () => {
    if (code) {
      toggleSubjectKey({ code, key });
    } else {
      insertSubject({ type, recordId, groupCodes, keys: [key] });
    }
  };

  const handleDeleteClick = (code) => () => {
    deleteSubject(code);
  };

  return (
    <Box sx={{ px: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={(theme) => ({
          alignItems: 'center',
          position: 'sticky',
          top: 48,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          zIndex: 700,
          pl: 1,
          py: 1,
        })}
      >
        <Typography variant="h6">Suspects, Recalls & Warrants</Typography>
        <Box>
          <Tooltip title="Done" placement="right">
            <IconButton onClick={handleCompleteClick}>
              <DoneIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box>
          <Tooltip title="Refresh" placement="bottom">
            <Box component="span">
              <IconButton
                onClick={handleRefreshClick}
                disabled={
                  isFetchingSuspects ||
                  isLoadingSuspects ||
                  isFetchingPrisonRecalls ||
                  isLoadingPrisonRecalls ||
                  isFetchingWarrants ||
                  isLoadingWarrants ||
                  isFetchingHighPrioritySuspects ||
                  isLoadingHighPrioritySuspects
                }
              >
                <AutorenewIcon
                  fontSize="inherit"
                  sx={
                    isFetchingSuspects ||
                    isLoadingSuspects ||
                    isFetchingPrisonRecalls ||
                    isLoadingPrisonRecalls ||
                    isFetchingWarrants ||
                    isLoadingWarrants ||
                    isFetchingHighPrioritySuspects ||
                    isLoadingHighPrioritySuspects
                      ? { animation: `${spin} 2s linear infinite` }
                      : undefined
                  }
                />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Stack>
      <Stack spacing={1} sx={{ pb: 1 }} ref={ref}>
        {(highPrioritySuspects?.length > 0 ||
          isLoadingHighPrioritySuspects ||
          isFetchingHighPrioritySuspects) && (
          <Fragment>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ pl: 1 }}>
                High Priority Suspects
              </Typography>
              <LoadingIcon
                isLoading={
                  isLoadingHighPrioritySuspects ||
                  isFetchingHighPrioritySuspects
                }
              />
            </Stack>
            <Masonry spacing={1} columns={highPriorityColumns}>
              {highPrioritySuspects.map(({ code, recordId }) => (
                <HighPrioritySuspect
                  key={recordId}
                  recordId={recordId}
                  onOptionClick={handleOptionClick}
                  onShowMapClick={handleShowMapClick({
                    type: 'SUSPECT',
                    recordId,
                  })}
                  onDeleteClick={handleDeleteClick(code)}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {(outstandingSuspects.length > 0 ||
          isLoadingSuspects ||
          isFetchingSuspects) && (
          <Fragment>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ pl: 1 }}>
                Outstanding Suspects
              </Typography>
              <LoadingIcon
                isLoading={isLoadingSuspects || isFetchingSuspects}
              />
            </Stack>
            <Masonry spacing={1} columns={lowPriorityColumns}>
              {outstandingSuspects.map((suspect) => (
                <Suspect
                  key={suspect.id}
                  suspect={suspect}
                  onOptionClick={handleOptionClick}
                  onShowMapClick={handleShowMapClick({
                    type: 'SUSPECT',
                    recordId: suspect.id,
                  })}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {(prisonRecalls?.length > 0 ||
          isLoadingPrisonRecalls ||
          isFetchingPrisonRecalls) && (
          <Fragment>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ pl: 1 }}>
                Prison Recalls
              </Typography>
              <LoadingIcon
                isLoading={isLoadingPrisonRecalls || isFetchingPrisonRecalls}
              />
            </Stack>
            <Masonry spacing={1} columns={lowPriorityColumns}>
              {prisonRecalls.map((recall) => (
                <PrisonRecall
                  key={recall.id}
                  recall={recall}
                  onOptionClick={handleOptionClick}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {(outstandingWarrants?.length > 0 ||
          isLoadingWarrants ||
          isFetchingWarrants) && (
          <Fragment>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ pl: 1 }}>
                Outstanding Warrants
              </Typography>
              <LoadingIcon
                isLoading={isLoadingWarrants || isFetchingWarrants}
              />
            </Stack>
            <Masonry spacing={1} columns={lowPriorityColumns}>
              {outstandingWarrants.map((warrant) => (
                <Warrant
                  key={warrant.id}
                  warrant={warrant}
                  onOptionClick={handleOptionClick}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
      </Stack>
      <LocationMapDialog
        point={selectedRecord?.addresses?.[0].point}
        title={`${selectedRecord?.forenames} ${selectedRecord?.surname} home address`}
        description={selectedRecord?.addresses?.[0]?.label}
        isOpen={!!selectedRecord}
        onClose={handleMapClose}
      />
    </Box>
  );
}
